<template>
<div class="card">
    <div class="card-header">Make Offer</div>
    <div class="card-body">
        <form-summary :validator="$v" ref="errorContainer" id="errorContainer"/>

        <div v-if="hiringManagerOrSubmitter">   
            <BaseStatusHelp lifecycleStatus="35"/>
                    
            <b-row>
                <b-col>
                    <LifecycleCheckList lifecycleStatus="35" :requestId="request.id"
                        and v-if="!isUnderReview"
                        v-on:checklist-update="updateChecklistStatus($event)" ref="checklist"/>
                </b-col>
            </b-row>

            <b-form @submit.stop.prevent="onSubmit">
                <b-row>
                    <b-col cols="12" lg="4">
                        <BaseSelector v-model="request.offerMethod" 
                            :v="$v.request.offerMethod"
                            label="Offer Making Method"
                            slug="offerMethod"
                            :options = offerMethodOptions />
                    </b-col>
                    <b-col cols="12" lg="4">
                        <BaseDateInput v-model="request.contingentOfferMadeDate" 
                                :v="$v.request.contingentOfferMadeDate"
                                label="Contingent Offer Made Date"
                                slug="offer-date" />
                    </b-col>
                    <b-col cols="12" lg="4">
                        <BaseSelector v-model="request.offerOutcome" 
                            :v="$v.request.offerOutcome"
                            label="Offer Decision"
                            slug="offerOutcome"
                            :options = offerOutcomeOptions />
                    </b-col>
                    <b-col cols="12" lg="4" v-show="offerDeclined">
                        <BaseSelector v-model="request.offerDeclineReason" 
                            v-show="offerDeclined"
                            :v="$v.request.offerDeclineReason"
                            label="Decline Reason"
                            slug="declineReason"
                            :options = offerDeclineOptions />                            
                    </b-col>
                    <b-col cols="12" lg="4">
                        <BaseSelector v-model="request.offerNextStep" 
                            :v="$v.request.offerNextStep"
                            label="Next Steps"
                            slug="nextSteps"
                            :options = offerNextStepsOptions />
                    </b-col>
                    <b-col cols="12" lg="4">
                        <BaseDateInput v-model="request.effectiveStartDate" 
                                   :v="$v.request.effectiveStartDate"
                                   label="Effective Start Date"
                                   slug="effective-start-date"/>
                    </b-col>
                     <b-col cols="12" lg="8">
                        <BaseTextInput v-model="request.offerOtherText" 
                            v-show="needOtherText"
                            :v="$v.request.offerOtherText"
                            label="Please provide additional information"
                            slug="otherText" />
                    </b-col>                    
                </b-row>
                
                <div>
                    <b-button type="button" variant="primary" @click="onSubmitOffer">Complete Make Offer</b-button>   
                    <b-button type="button" variant="outline-primary" @click="save">Save</b-button>
                </div>
            </b-form>
        </div>
        <div v-else>
            <b-col cols="12">
                <b-alert show>This request is currently being reviewed by the Requester.  Please wait their response.</b-alert>
            </b-col>
        </div>
    </div>
</div>

</template>

<script>
import axios from "axios"
import { required, email, minLength, maxLength, numeric, requiredIf } from 'vuelidate/lib/validators'
import LifecycleCheckList from '@/components/lifecycles/LifecycleCheckList'
import { commonOptions } from '@/mixins/commonOptions.js'
import _ from "lodash"
import {roleService} from "@/mixins/roleService";


const terms = ['TBD', 'tbd']
const tbdValidator = (value) => !terms.some(el => value && value.indexOf(el) > -1)

export default {
    components: {
        LifecycleCheckList
    },
    mixins: [
       commonOptions,
       roleService
    ],
    props: {
        request: Object
    },
    data() {
        return {
            funding: this.request.funding,            
            comments: '',
            checklistComplete: false,
            hiringManagerOrSubmitter: false,
            saveOnly: false,
            totalFundingStringPercent: 'valid',
            offerDifferentCandidateComment: null,
            isOfferDifferentCandidate: false,
            defaultFunding: {
                id: null, 
                fundingPercent: null, 
                fundId: '',
                divisionId: '',
                appropriationId: '',
                swCost: '',
                subAccount: '',
                agcyCost1: '',
                agcyCost2: '',
                project: '',
                activity: '',
                sourceType: ''                    
            },     
        }
    },
     validations: {
         checklistComplete: { checked: value => value === true },
         comments: { required: false, maxLength: maxLength(4000) },
         request: {
             contingentOfferMadeDate: { required },
             offerMethod: { required },
             offerOutcome: { required },
             offerDeclineReason: {  required: requiredIf(function() {
                        return (this.request.offerOutcome && this.request.offerOutcome === 'Decline')
                               
                }) },
             offerNextStep: { required },
             effectiveStartDate: {required: false},
             offerOtherText: {
                required: false,
                maxLength: maxLength(100)
             }
        },
        totalFundingStringPercent: {
            required,
        },
        offerDifferentCandidateComment:{
          required: requiredIf(function() {
            return this.isOfferDifferentCandidate
          }) , maxLength: maxLength(4000)
        },
        funding: {
            required,
            minLength: minLength(1),
            $each: {
                fundingPercent: {
                    required,
                    numeric
                },
                fundId: {
                    required,
                    tbdValidator
                },
                divisionId: {
                    required,
                    tbdValidator
                },
                sourceType: {
                    required: requiredIf(function(item) {
                        return item.project || item.activity
                    }),
                },
                appropriationId: {
                    required,
                    tbdValidator
                },
                project: {
                    required: requiredIf(function(item) {
                        return ((item.fundId === '3000' || item.fundId === '3001') ||
                                (item.activity || item.sourceType))
                    }),
                    tbdValidator
                },
                activity: {
                    required: requiredIf(function(item) {
                        return item.project || item.sourceType
                    }),
                    maxLength: maxLength(15),
                    tbdValidator
                }
            },            
        },        
     },
    watch: {
        funding: {
            deep: true,
             handler() {
                 this.totalFundingStrings()
             }
        },
        request: {
          deep: true,
          handler() {
              if (this.request.offerOutcome && this.request.offerOutcome === 'Accept') {
                this.request.offerDeclineReason = null 
              }
              if (this.request.offerDeclineReason != 'Other' && this.request.offerNextStep != 'Other') {
                this.request.offerOtherText = null
              }
          }
        }
    },
    computed: {
       offerDeclined: function() {
            return (this.request.offerOutcome && this.request.offerOutcome === 'Decline')
        },
       needOtherText: function() {
           return (this.request.offerDeclineReason && this.request.offerDeclineReason === 'Other') ||
                            (this.request.offerNextStep && this.request.offerNextStep === 'Other')
       },

        isUnderReview: function() {
            return (this.isFfm() &&
                this.request.financeRequiresReview === true)
        },

    },
    created() {
        if (this.request.financeRequiresReview === true) {
            this.checklistComplete = true
            this.comments = this.request.fmReviewComments
        } else {
            this.comments = this.request.offerComments
        }

        this.hiringManagerOrSubmitter = (this.request.supervisor && this.request.supervisor.userId &&
                                        this.request.supervisor.userId.toLowerCase() === this.$keycloak.userName.toLowerCase()) ||
                                        (this.request.submitter && this.request.submitter.userId &&
                                        this.request.submitter.userId.toLowerCase() === this.$keycloak.userName.toLowerCase())
    },

    methods: {
        totalFundingStrings() {
            var total = _.sumBy(this.funding, item => Number(item.fundingPercent))
            this.totalFundingStringPercent = total === 100 ? "valid" : ""
        },
        updateChecklistStatus(b) {
            this.checklistComplete = b
        },
        addFundingStringRow() {
            if (!this.funding) {
                this.funding = []
            }
            var newFunding = {... this.defaultFunding}
            this.funding.push(newFunding)
        },
        removeFundingStringRow(idx) {
            if (this.request.funding && this.request.funding.length > 1) {
                this.request.funding.splice(idx, 1)
            }
        },

        save() {
            this.saveOnly = true
            this.onSubmitOffer()
            this.saveOnly = false
        },

        onSubmitSave() {
            this.totalFundingStrings()
            this.$v.funding.$touch()
            this.$v.totalFundingStringPercent.$touch()
            if (this.$v.funding.$invalid || this.$v.totalFundingStringPercent.$invalid) {
                this.$nextTick(() => {
                    document.getElementById('form-container').scrollIntoView()
                })
                return
            }

            let payload = {
                id: this.request.id,
                funding: this.funding,
                comments: this.request.fmReviewComments
            }

            axios.post(`${process.env.VUE_APP_API_BASE_URL}/request/fmReview/save`, payload)
                  .then(response => {            
                    this.showRootToast("Updated", "Your changes has been recorded.", "success")
                    this.$router.push('/home')
                  })
        },


        onSubmitReviewOffer() {
            this.totalFundingStrings()
            this.$v.funding.$touch()
            this.$v.totalFundingStringPercent.$touch()
            if (this.$v.funding.$invalid || this.$v.totalFundingStringPercent.$invalid) {
                this.$nextTick(() => {
                    document.getElementById('form-container').scrollIntoView()
                })
                return
            }

            let payload = {
                id: this.request.id,
                funding: this.funding,
                comments: this.request.fmReviewComments
            }

            axios.post(`${process.env.VUE_APP_API_BASE_URL}/request/fmReview`, payload)
                  .then(response => {            
                    this.showRootToast("Updated", "Your review has been recorded.", "success")
                    this.$router.push('/home')
                  })
        },

        onSubmitOffer() {
            if (!this.saveOnly) {
                this.$refs.checklist.emitCheckListStatus()
                this.$v.$touch();
                if (this.$v.$invalid) {
                    this.$nextTick(() => {
                        document.getElementById('form-container').scrollIntoView()
                    })
                    return
                }
            }

            let payload = {
                requestId: this.request.id,
                offerMethod: this.request.offerMethod,
                offerOutcome: this.request.offerOutcome,
                offerDeclineReason: this.request.offerDeclineReason,
                offerNextStep: this.request.offerNextStep,
                offerOtherText: this.request.offerOtherText,
                effectiveStartDate: this.request.effectiveStartDate,
                contingentOfferMadeDate: this.request.contingentOfferMadeDate,
                advanceNextLifecycle: !this.saveOnly
            }
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/manager/makeoffer`, payload)
                  .then(response => { 
                    this.showRootToast("HRM Request Has Been Updated", "Success", "success")
                    if (payload.advanceNextLifecycle === true) {
                        this.$router.push('/home')
                    } 
                  })                  
        },

        onReturnToPriorLifecycle() {
            let payload = {
                id: this.request.id,
                transitionStatusTo: '11',
                comment: this.offerDifferentCandidateComment
            }
            console.log("We are going back with this as the comment:",this.offerDifferentCandidateComment)
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/request/transition`, payload)
                  .then(response => { 
                    this.showRootToast("HRM Request Has Been Updated", 
                        "Success", "success")
                     this.$router.push('/home')
                  })                  
        },

        showRootToast(title, message, type) {
            this.$root.$bvToast.toast(message, {
                  title: title,
                  toaster: 'b-toaster-top-center',
                  solid: true,
                  variant: type,
                  appendToast: true
                })
        },

        sendOfferLetterToRequester() {
             let payload = {
                requestId: this.request.id,
                nextLifecycle: '35'
            }
        
            axios.post(`${process.env.VUE_APP_API_BASE_URL}/manager/return/requester`, payload)
              .then(response => {
                this.showRootToast("HRM Request Has Been Sent to Reqester", "Success", "success")
                this.$router.push('/home')
              })
        }
    }   
}
</script>